import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as authClient from '@/services/authClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      const currentUser = await authClient.getCurrentUser();
      setUser(currentUser);
      return currentUser;
    } catch (error) {
      setUser(null);
      return null;
    }
  }, []);

  /**
   * Tenant-host login only (password never sent to public API).
   * Returns login JSON (includes redirect_url when provided by backend).
   */
  const loginOnTenant = useCallback(async (tenant, username, password) => {
    const payload = await authClient.loginOnTenant(tenant, username, password);
    return payload;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authClient.logout();
    } finally {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshSession();
      setIsLoading(false);
    })();
  }, [refreshSession]);

  const value = useMemo(() => ({
    user,
    isAuthenticated: Boolean(user),
    isLoading,
    loginOnTenant,
    logout,
    refreshSession,
  }), [user, isLoading, loginOnTenant, logout, refreshSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
